import React, { useContext } from "react";
import {Box, Button, Flex, Text} from "rebass";
import {Form, Formik, useField} from "formik";
import {
  CardFooter,
  Modal,
  TextInput,
  DateInput,
  RadioInput, SelectInput, LabeledText, ParagraphInput, NumberInput, NumberBox,
} from "../../Components";
import {PatientsContext, TourContext} from "../../Contexts";
import {Patient, PatientCreate} from "../../Types";
import moment from "moment";
import {InputHelper, InputHelperOptionalParameters} from "../../Components/Inputs/InputHelper";
import styled from "styled-components";

export const AddPatientModal: React.FC<{
  isOpen: boolean;
  setIsOpen: (b: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const patientContext = useContext(PatientsContext);


  const defaultUserValues: PatientCreate = {
    dob: "",
    user_name: "",
    //dominant_eye: "Right",
    has_amblyopia: 0,
    has_strabismus: 0,
  };

  // use demo user during tour
  const tourContext = useContext(TourContext);
  defaultUserValues.user_name = tourContext.defaults.addPatientName
  defaultUserValues.dob = tourContext.defaults.addPatientDate


  const onSubmit = async (values) => {
    await patientContext.createPatient(values)
    setIsOpen(false)
    tourContext.callbacks["onCreatePatient"]()
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} headerText="Add a new Patient" dataTour="addPatientModal" overflow="show">
      <PatientForm onSubmit={(values) => onSubmit(values)} defaultUserValues={defaultUserValues} submitLabel={"Create Patient"}/>
    </Modal>
  );
};

export const EditPatientModal: React.FC<{
  patient: Patient | undefined;
  setPatient: (any) => void;
}> = ({ patient, setPatient }) => {

  const patientContext = useContext(PatientsContext);

  if (patient === undefined) {
    return <></>
  }

  console.log("dob", patient.dob)

  const defaultUserValues: PatientCreate = {
    dob: patient.dob? moment.unix(+patient.dob).format("YYYY/MM/DD") : "",
    user_name: patient.user_name,
    /*dominant_eye: patient.dominant_eye,
    has_amblyopia: patient.has_amblyopia,
    has_strabismus: patient.has_strabismus,
    gender: patient.gender,
    ethnicity: patient.ethnicity,
    notes: patient.notes*/
  };


  const onSubmit = async (values) => {
    await patientContext.editPatient(patient.clinic_user_id, values)
    setPatient(undefined)
  };


  const header=(
    <Flex>
      <Text textAlign="center" fontWeight="bold" fontSize={[2, 4]}>Edit Patient - </Text>
      <Text fontWeight="bold" color="darkBlue" fontSize={[2, 4]} paddingLeft={1}>Vivid Vision ID</Text>
      <Text fontWeight="bold" color="secondary" fontSize={[2, 4]} paddingLeft={1}>{'#' + patient.clinic_user_id}</Text>
    </Flex>
  )

  return (
    <Modal isOpen={patient !== undefined} setIsOpen={(isOpen => isOpen? setPatient(patient) : setPatient(undefined))}
           headerElement={header} dataTour="addPatientModal" overflow="show"
    >
      <PatientForm onSubmit={(values) => onSubmit(values)} defaultUserValues={defaultUserValues} submitLabel={"Apply Changes"}/>
    </Modal>
  );
};

export const PatientForm: React.FC<{
  onSubmit: (any) => void;
  defaultUserValues: any;
  submitLabel: string;
}> = ({ onSubmit, defaultUserValues, submitLabel}) => {

  return (
      <Formik onSubmit={onSubmit} initialValues={defaultUserValues}>
        <Form>
          <Text fontWeight="bold" fontSize={2} marginLeft={3} marginBottom={3}>Mandatory Fields</Text>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ["1fr", "1fr 1fr", "1fr 1fr 1fr"],
              columnGap: 3,
              rowGap: 3,
              justifyItems: "left",
            }}
          >
            <TextInput helperParameters={{label: "User Name", inlineLabel: false}} name="user_name" errorText="Please select a user name"/>
            <DateInput helperParameters={{label: "Date of Birth", inlineLabel: false}} name="dob" noFutureSelect={true}/>
          </Box>
          <div style={{borderTop: "1px solid #e7e7e7", width: "850px", margin: "23px"}}></div>
          <Text fontWeight="bold" fontSize={2} marginLeft={3} marginBottom="16px">Optional Fields</Text>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ["1fr", "1fr 1fr", "1fr 1fr 1fr"],
              columnGap: 3,
              rowGap: 3,
              justifyItems: "left",
            }}
          >
            <TextInput
              name={"custom_id"}
              helperParameters={{label: "Custom Patient ID", inlineLabel: false}}

            />
            <RadioInput
              helperParameters={{label: "Dominant Eye", inlineLabel: false}}
              options={["Right", "Left"]}
              name="dominant_eye"
              allowEmpty={false}
            />

            <RadioInput
              helperParameters={{label: "Does this person have Amblyopia?", inlineLabel: false}}
              name="has_amblyopia"
              options={[1, 0]}
              labels={["Yes", "No"]}
              allowEmpty={false}
            />

            <RadioInput
              helperParameters={{label: "Does this person have Strabismus?", inlineLabel: false}}
              name="has_strabismus"
              options={[1, 0]}
              labels={["Yes", "No"]}
              allowEmpty={false}
            />

            <SelectInput
              helperParameters={{label: "Gender", inlineLabel: false}}
              name="gender"
              options={["Male", "Female", "Nonbinary"]}
              allowEmpty={true}
            />

            <SelectInput
              helperParameters={{label: "Ethnicity", inlineLabel: false}}
              name="ethnicity"
              options={["White", "Black or African American", "American Indian or Alaska Native", "Asian", "Native Hawaiian or Other Pacific Islander",  "Other"]}
              allowEmpty={true}
            />

            <ParagraphInput
              helperParameters={{label: "Patient Notes", inlineLabel: false}}
              name="notes"
              allowEmpty={true}
            />

            <RefractionCorrectionInputs
              helperParameters={{label: "Refraction Correction", inlineLabel: false}}
              name="refraction_correction"
            />
          </Box>
          <CardFooter>
            <Button type="submit">{submitLabel}</Button>
          </CardFooter>
        </Form>
      </Formik>
  );
};

const RefractionCorrectionInputs: React.FC<{
  name: string;
  helperParameters?: InputHelperOptionalParameters;
}> = ({name, helperParameters}) => {
  const validate = (value) => {};

  const [field, meta, helpers] = useField({name, validate });

  const defaultValue = {
    OD_SPH: null,
    OD_CYL: null,
    OD_AXIS: null,

    OS_SPH: null,
    OS_CYL: null,
    OS_AXIS: null
  }

  const value = meta.value || defaultValue

  console.log(value)

  const RefractionNumberBox: React.FC<{
    id: string;
    placeholder?: string;
  }> = ({id, placeholder}) => {
    let dp = id.includes("AXIS") ? 0 : 2;
    return (
      <NumberBox value={value[id]} placeholder={placeholder} decimalPlaces={dp} setValue={(newValue) => {
        value[id] = newValue
        helpers.setValue(value)
      }}/>)
  }

  return <InputHelper meta={meta} helpers={helpers} {...helperParameters} >
    <table style={{borderCollapse: "collapse"}}>
      <Row>
        <Column>Rx</Column>
        <Column>SPH</Column>
        <Column>CYL</Column>
        <Column rightEdge={true}>AXIS</Column>
      </Row>

      <Row>
        <Column>O.D.</Column>
        <Column><RefractionNumberBox id="OD_SPH"/></Column>
        <Column><RefractionNumberBox id="OD_CYL"/></Column>
        <Column rightEdge={true}><RefractionNumberBox id="OD_AXIS" placeholder=""/></Column>
      </Row>

      <Row>
        <Column bottomEdge={true}>O.S.</Column>
        <Column bottomEdge={true}><RefractionNumberBox id="OS_SPH"/></Column>
        <Column bottomEdge={true}><RefractionNumberBox id="OS_CYL"/></Column>
        <Column bottomEdge={true} rightEdge={true}><RefractionNumberBox id="OS_AXIS" placeholder=""/></Column>
      </Row>
    </table>
  </InputHelper>
}


const Row = styled.tr<{}>`
  `

const Column = styled.td<{rightEdge?: boolean, bottomEdge?: boolean}>`
    color: #2A6C8A;
    font-weight: bold;
    border-right: ${props => props.rightEdge===true? "0":"solid 1px #dddddf"};
    border-bottom: ${props => props.bottomEdge===true? "0":"solid 1px #dddddf"};
    padding: 0.5rem;
    text-align: center;
  `
